/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Container, Form, InputGroup } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { authMe, authenticate } from '../data';
import { useSoundManager } from './SoundContext';

const logo = '/glasswing_icon.svg';

function normalizeLoginError(err: any): string {
    const raw = String(err?.message || '').trim();
    if (!raw) {
        return 'Sign-in failed. Please try again.';
    }

    const stripPrefix = raw.replace(/^Error:\s*/i, '');
    const extractJson = stripPrefix.match(/\{[\s\S]*\}$/);
    if (extractJson) {
        try {
            const parsed = JSON.parse(extractJson[0]);
            if (typeof parsed?.error === 'string') {
                const apiMsg = parsed.error.toLowerCase();
                if (apiMsg.includes('invalid credentials')) {
                    return 'Username or password is incorrect.';
                }
                if (apiMsg.includes('required')) {
                    return 'Please enter both username and password.';
                }
                if (apiMsg.includes('temporarily locked')) {
                    return 'Too many attempts. Please wait a few minutes and try again.';
                }
                return parsed.error;
            }
        } catch {
            // fall through to generic handling
        }
    }

    const lower = stripPrefix.toLowerCase();
    if (lower.includes('http 401') || lower.includes('invalid credentials')) {
        return 'Username or password is incorrect.';
    }
    if (lower.includes('http 429') || lower.includes('temporarily locked')) {
        return 'Too many attempts. Please wait a few minutes and try again.';
    }
    if (lower.includes('http 404')) {
        return 'Sign-in service is unavailable right now.';
    }
    return 'Sign-in failed. Please try again.';
}

function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme() as any;
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pwRef = useRef<HTMLInputElement | null>(null);
    const soundManager = useSoundManager();

    const nextPath = searchParams.get('next') || '/';

    useEffect(() => {
        let active = true;
        authMe()
            .then(() => {
                if (active) {
                    navigate(nextPath, { replace: true });
                }
            })
            .catch(() => {
                // remain on login page when unauthenticated
            });
        return () => {
            active = false;
        };
    }, [navigate, nextPath]);

    const doLogin = async () => {
        if (!username.trim() || !password) {
            setError('Please enter both username and password.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await authenticate(username.trim(), password);

            // Never block sign-in completion on settings I/O.
            void Promise.race([
                soundManager.reloadSettings(),
                new Promise<void>((resolve) => {
                    window.setTimeout(resolve, 500);
                }),
            ]).catch(() => {
                // best effort only
            });

            void soundManager.playSound('login', { force: true });
            navigate(nextPath, { replace: true });
        } catch (e: any) {
            setError(normalizeLoginError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container fluid className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>
            <Card style={{ width: 'min(95vw, 440px)', border: `1px solid ${theme.colors.ctext}`, backgroundColor: theme.colors.bg }}>
                <Card.Body>
                    <div className="text-center mb-4">
                        <img src={logo} alt="Fbot" style={{ width: 120, height: 120, objectFit: 'contain' }} />
                    </div>
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            doLogin();
                        }}
                    >
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: theme.colors.text }}>Username</Form.Label>
                            <Form.Control
                                autoFocus
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="username or email"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        pwRef.current?.focus();
                                    }
                                }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ color: theme.colors.text }}>Password</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    ref={pwRef}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    type={showPassword ? 'text' : 'password'}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            doLogin();
                                        }
                                    }}
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowPassword((v) => !v)}
                                    title={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </Button>
                            </InputGroup>
                        </Form.Group>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={busy || !username.trim() || !password}
                            className="w-100"
                        >
                            {busy ? 'Signing in...' : 'Login'}
                        </Button>
                        {error ? (
                            <div className="mt-2 small" style={{ color: theme.colors.textMuted || '#9aa0a6' }}>
                                {error}
                            </div>
                        ) : null}
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default LoginPage;
